import { useEffect, useRef, useState } from "react";
import { CreditCard, ExternalLink, RefreshCw } from "lucide-react";

import { useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";

const button = "inline-flex min-h-10 items-center justify-center gap-2 rounded border border-white/20 px-4 py-2 text-sm disabled:opacity-50";
const pounds = (pence) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
const date = (instant) => new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", dateStyle: "medium" }).format(new Date(instant));

export function stripeBillingUrl(value, kind) {
  const target = new URL(value);
  const host = kind === "checkout" ? "checkout.stripe.com" : "billing.stripe.com";
  if (target.protocol !== "https:" || target.hostname !== host || target.port || target.username || target.password || (kind !== "checkout" && target.hash)) {
    throw new Error("The billing provider returned an unexpected destination.");
  }
  return target.href;
}

export default function PremiumWebBilling({ returnState = "", navigate = (url) => window.location.assign(url) }) {
  const auth = useConsumerAuth();
  const owner = auth.account?.user?.user_id;
  return <OwnedWebBilling key={owner || "signed-out"} auth={auth} owner={owner} returnState={returnState} navigate={navigate} />;
}

function OwnedWebBilling({ auth, owner, returnState, navigate }) {
  const enabled = Boolean(owner && auth.ready && auth.account.subscription?.billing_enabled === true);
  const [membership, setMembership] = useState(auth.account?.subscription);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const current = useRef({ active: true, owner, generation: 0, busy: false });
  const session = useRef(auth.csrfToken);
  const liveAuth = useRef({ enabled, token: auth.csrfToken });
  liveAuth.current = { enabled, token: auth.csrfToken };

  useEffect(() => {
    const state = current.current;
    state.active = true;
    return () => { state.active = false; state.generation += 1; };
  }, []);
  useEffect(() => {
    if (session.current !== auth.csrfToken) {
      session.current = auth.csrfToken;
      current.current.generation += 1;
      current.current.busy = false;
      setWorking(false);
      setError("");
    }
  }, [auth.csrfToken]);

  useEffect(() => {
    setMembership(auth.account?.subscription);
  }, [auth.account?.subscription]);

  useEffect(() => {
    if (!enabled) {
      current.current.generation += 1;
      current.current.busy = false;
      setWorking(false);
      setLoading(false);
      return;
    }
    const generation = ++current.current.generation;
    let cancelled = false;
    setLoading(true);
    consumerApi.get("/premium/billing/subscription").then(({ data }) => {
      if (!cancelled && current.current.active && current.current.generation === generation) setMembership(data);
    }).catch(() => {
      if (!cancelled && current.current.active && current.current.generation === generation) setError("Subscription status could not be loaded.");
    }).finally(() => {
      if (!cancelled && current.current.active && current.current.generation === generation) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [enabled, auth.csrfToken]);

  const act = async (kind) => {
    if (!enabled || current.current.busy) return;
    if (kind !== "refresh" && membership?.billing_enabled !== true) return;
    current.current.busy = true;
    const generation = ++current.current.generation;
    const token = auth.csrfToken;
    const isCurrent = () => current.current.active && current.current.owner === owner && current.current.generation === generation && liveAuth.current.enabled && liveAuth.current.token === token;
    setWorking(true);
    setLoading(false);
    setError("");
    try {
      if (kind === "refresh") {
        const { data } = await consumerApi.get("/premium/billing/subscription");
        if (!isCurrent()) return;
        setMembership(data);
        await auth.refresh();
      } else {
        const result = await auth.mutate("post", `/premium/billing/${kind}`, {});
        if (!isCurrent()) return;
        navigate(stripeBillingUrl(result.url, kind));
      }
    } catch (failure) {
      if (isCurrent()) setError(typeof failure?.response?.data?.detail === "string"
        ? failure.response.data.detail : failure.message || "Billing could not be opened. No new access has been granted.");
    } finally {
      if (isCurrent()) { current.current.busy = false; setWorking(false); }
    }
  };
  const status = membership?.status;
  const subscribed = Boolean(membership?.product_key || membership?.entitlement_active);

  return <section aria-label="Premium subscription">
    <header className="mb-6"><h2 className="font-display text-2xl font-semibold">Subscription</h2><p className="mt-2 text-sm text-[var(--cc-text-muted)]">Competition Checker Premium is available to UK customers. Individual operators may have additional territorial restrictions.</p></header>
    <div className="border-y border-white/10 py-5">
      <p className="text-sm text-white/45">Current membership</p><p className="mt-1 font-display text-xl font-semibold">{membership?.entitlement_active ? "Premium" : "Free"}</p>
      {status && <p className="mt-2 text-sm">{status.replaceAll("_", " ")}</p>}
      {membership?.trial_ends_at && <p className="mt-2 text-sm">Trial ends {date(membership.trial_ends_at)} (UK time).</p>}
      {membership?.effective_until && <p className="mt-2 text-sm">Current access ends {date(membership.effective_until)} (UK time).</p>}
      {membership?.cancel_at_period_end && <p className="mt-2 text-sm">Your subscription will not renew after the current period.</p>}
    </div>
    {!enabled ? <p className="mt-5 text-sm text-[var(--cc-text-muted)]">Premium checkout is currently unavailable. Your basic account remains accessible.</p> : <>
      {!subscribed && <div className="py-5"><h3 className="font-display text-lg font-semibold">14-day trial</h3><p className="mt-2 text-sm leading-relaxed">Card required. Then {pounds(249)} per month for the first three paid months, followed by {pounds(499)} per month. Cancel before the trial ends to avoid payment. Trial eligibility and final charges are confirmed at checkout.</p></div>}
      {returnState === "success" && <p className="mt-5 text-sm" role="status">Checkout returned. Premium access is granted only after the provider confirms your subscription.</p>}
      {returnState === "cancelled" && <p className="mt-5 text-sm" role="status">Checkout was cancelled. No subscription is assumed.</p>}
      {loading && <p className="mt-4 text-sm" role="status">Checking subscription...</p>}
      {error && <p className="mt-4 text-sm text-red-200" role="alert">{error}</p>}
      <div className="mt-5 flex flex-wrap gap-3">
        <button type="button" className={button} disabled={working || loading || membership?.billing_enabled !== true || status === "UNAVAILABLE"} onClick={() => void act(subscribed ? "portal" : "checkout")}><CreditCard size={17} />{subscribed ? "Manage web subscription" : "Continue to Stripe"}<ExternalLink size={15} /></button>
        <button type="button" className={button} disabled={working || loading} onClick={() => void act("refresh")}><RefreshCw size={17} />Refresh status</button>
      </div>
      {subscribed && <p className="mt-3 text-sm text-[var(--cc-text-muted)]">Subscriptions bought in Google Play or the App Store are managed in that store.</p>}
    </>}
  </section>;
}
